import { requestAdminInvoices } from '../../requests/reports/invoices/admin-invoice-requests';
import { coerceCount } from '../../requests/generic-requests';
import { apiDateRange } from './requests/apiDates';
import { asksForTimeComparison } from './comparisonQuestion';
import { formatCurrencyValue } from '../../util/PaymentUtils';
import type { KnowledgeAnswer, KnowledgeContext, KnowledgeQuery } from './knowledgeRegistry';

/**
 * Ranks supplier companies by what they earned over a period.
 *
 * Unlike the generated count skills, this cannot be answered by a single
 * server-side aggregate: the admin invoice `group-summary-report` endpoint
 * exists but has no consumer anywhere in this codebase, so its parameters and
 * response shape are unverifiable from here. Guessing at them would produce a
 * confident, wrong revenue figure, so the totals are summed from invoice rows
 * instead, which are typed and therefore checkable.
 */

/**
 * Rows per request. The API rejects anything above 100 ("Page size must be
 * between 1 and 100", code 115003), so this is the largest page it will serve.
 */
const PAGE_SIZE = 100;

/**
 * Hard ceiling on requests for one question, so a wide date range cannot fire
 * an unbounded burst at the API.
 */
const MAX_PAGES = 50;

/** How many runners-up to name alongside the leader. */
const RUNNERS_UP = 2;

interface CompanyTotal {
  company: string;
  /** Integer cents. Summed as integers so the total cannot drift. */
  cents: number;
  invoices: number;
}

interface RevenueTally {
  ranked: CompanyTotal[];
  rowsRead: number;
  rowsAvailable: number;
  /** True when the ceiling stopped us before we had seen every invoice. */
  truncated: boolean;
}

interface InvoiceRow {
  supplierCompanyName?: string;
  supplierAmount?: number;
}

const tallyRow = (totals: Map<string, CompanyTotal>, row: InvoiceRow): void => {
  const company = row.supplierCompanyName?.trim();
  // A row with no company cannot be attributed. Counting it under a placeholder
  // would invent a competitor; dropping it keeps the ranking honest.
  if (!company) return;
  const amount = typeof row.supplierAmount === 'number' ? row.supplierAmount : 0;
  const existing = totals.get(company);
  if (existing) {
    existing.cents += amount;
    existing.invoices += 1;
    return;
  }
  totals.set(company, { company, cents: amount, invoices: 1 });
};

const collectSupplierRevenue = async (range: {
  startDate?: string;
  endDate?: string;
}): Promise<RevenueTally> => {
  const totals = new Map<string, CompanyTotal>();
  let rowsRead = 0;
  let rowsAvailable = 0;
  let page = 0;

  while (page < MAX_PAGES) {
    const response = await requestAdminInvoices({ ...range, page, size: PAGE_SIZE });
    const body = (response?.body ?? {}) as { content?: InvoiceRow[] };
    rowsAvailable = coerceCount(response?.body) ?? rowsAvailable;

    const rows = Array.isArray(body.content) ? body.content : [];
    if (rows.length === 0) break;

    rows.forEach((row) => tallyRow(totals, row));
    rowsRead += rows.length;
    page += 1;

    if (rowsRead >= rowsAvailable) break;
  }

  const ranked = Array.from(totals.values()).sort(
    // Ties broken by name so repeating the question cannot reorder the answer.
    (a, b) => b.cents - a.cents || a.company.localeCompare(b.company),
  );

  return {
    ranked,
    rowsRead,
    rowsAvailable,
    truncated: rowsAvailable > rowsRead,
  };
};

const money = (cents: number): string => String(formatCurrencyValue(cents, true));

const describeEntry = (entry: CompanyTotal): string =>
  `**${entry.company}** \u2014 ${money(entry.cents)} across ` +
  `${entry.invoices} ${entry.invoices === 1 ? 'invoice' : 'invoices'}`;

const describeTally = (tally: RevenueTally, label: string): string => {
  if (tally.ranked.length === 0) {
    return `No supplier earned anything ${label}.`;
  }

  const [leader, ...rest] = tally.ranked;
  const runnersUp = rest.slice(0, RUNNERS_UP);

  /*
   * A truncated tally can name the *wrong* winner, not merely an imprecise
   * amount: invoices come back in the API's own order rather than by value, so
   * the rows we did not read are an arbitrary slice rather than a tail of small
   * ones. Presenting a leader from a partial read as though it were the answer
   * is the failure this whole skill exists to avoid, so say so first.
   */
  if (tally.truncated) {
    return (
      `I can only read ${tally.rowsRead.toLocaleString()} of the ` +
      `${tally.rowsAvailable.toLocaleString()} invoices ${label}, so I cannot tell you ` +
      'reliably which company earned the most \u2014 the invoices I did not read could ' +
      `change the order. Among the ones I read, the highest was ${describeEntry(leader)}. ` +
      'Ask about a shorter period for an answer I can stand behind.'
    );
  }

  const lead = `${describeEntry(leader)} \u2014 earned the most ${label}.`;
  if (runnersUp.length === 0) return lead;

  return `${lead} Next: ${runnersUp.map(describeEntry).join(', ')}.`;
};

export const topSupplierRevenueQuery: KnowledgeQuery = {
  id: 'adminTopSupplierRevenue',
  audiences: ['admin'],
  // One queue or one ranking now cannot say how it changed month to month.
  declines: asksForTimeComparison,
  synonyms: [
    'what company made the most revenue',
    'which company made the most revenue',
    'what company earned the most',
    'which company earned the most',
    'which supplier earned the most',
    'who made the most money',
    'highest earning company',
    'top earning company',
    'top supplier by revenue',
    // Deliberately no bare "revenue" or "how much revenue": those ask for a
    // total, and answering them with a single company's figure would be wrong.
  ],
  /**
   * Concept fallback for the phrasings the synonym list cannot enumerate:
   * "what/which/who" x "company/supplier/partner" x "made/earned/brought in" x
   * "money/revenue/income" is several hundred sentences for one question.
   *
   * Each group is kept deliberately narrow:
   * - "made" and "make" are **not** money words. They would match "who made the
   *   most bookings", which this skill cannot answer and would answer wrongly.
   * - "paid" is excluded too. "who paid the most" asks about a *buyer*, and this
   *   skill ranks suppliers by what they received — naming a supplier there
   *   would be a confidently wrong answer rather than a decline.
   */
  requires: [
    ['most', 'highest', 'top', 'biggest', 'largest'],
    ['money', 'revenue', 'revenues', 'earned', 'earnings', 'earning', 'income', 'grossed'],
    ['company', 'companies', 'supplier', 'suppliers', 'partner', 'partners', 'vendor', 'who'],
  ],
  example: 'what company made the most revenue last month',
  description:
    'Ranks supplier companies by how much they earned over a time period, for ' +
    'an administrator, and names the top earner. Uses the supplier\u2019s share of ' +
    'each invoice, which is what the supplier receives net of SecurSpace fees \u2014 ' +
    'not the gross amount transacted and not SecurSpace\u2019s own cut. Use for ' +
    '"which company earned the most" style questions. Not for a platform-wide ' +
    'total, and not for counting invoices.',
  answer: async ({ period }: KnowledgeContext): Promise<KnowledgeAnswer> => {
    const tally = await collectSupplierRevenue(apiDateRange(period));
    return { text: describeTally(tally, period.label) };
  },
};

export const __testing = { collectSupplierRevenue, describeTally, PAGE_SIZE, MAX_PAGES };
